import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EmptyStateProps } from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { DataTableEndlessPaginatedPageScroll } from '@/components/data-table/page-scroll';
import { Button } from '@/components/ui/button';
import { TableIds } from '@/lib/constants/table-ids';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { OverheadListItem } from '../../types/overhead';

interface OverheadsTableProps {
  table: Table<OverheadListItem>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  hasNonPeriodFiltersApplied?: boolean;
  totalCount?: number;
  onAddOverhead?: () => void;
  onResizeEnd?: () => void;
  /** Opens the edit modal (mirrors the pencil action). */
  onRowClick?: (overhead: OverheadListItem) => void;
  /** Right-click — opens the row context menu (copy link). */
  onRowContextMenu?: (overhead: OverheadListItem, event: React.MouseEvent) => void;
  /** Top offset (px) for the sticky column header — pins below the page chrome. */
  stickyHeaderTopPx?: number;
}

export const OverheadsTable: React.FC<OverheadsTableProps> = ({
  hasNonPeriodFiltersApplied,
  totalCount,
  onAddOverhead,
  onResizeEnd,
  onRowClick,
  onRowContextMenu,
  stickyHeaderTopPx,
  ...paginatedTableProps
}) => {
  const { t } = useTranslation(['authenticated.overheads', 'common']);
  const { hasPermission } = usePermissions();

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // The period alone should still show the primary empty state with actions.
    if (hasNonPeriodFiltersApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('table.noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    const isManageEnabled = hasPermission(OpsiumPolicy.OverheadsManage);

    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col gap-0.5">
                <div className="text-sm font-semibold text-foreground">
                  {t('table.noOverheadsYet')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('table.noOverheadsDescription')}
                </div>
              </div>
              {isManageEnabled && onAddOverhead && (
                <Button size="sm" className="h-9 gap-2" onClick={onAddOverhead}>
                  <CirclePlus className="h-4 w-4" />
                  {t('addOverhead')}
                </Button>
              )}
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginatedPageScroll
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      tableId={TableIds.OVERHEADS}
      onResizeEnd={onResizeEnd}
      onRowClick={onRowClick}
      onRowContextMenu={onRowContextMenu}
      stickyHeaderTopPx={stickyHeaderTopPx}
    />
  );
};
